import type { AgentMemory, WireMessage } from "./agentMemory";
import type { StreamOptions, StreamOutcome, StreamRequest } from "./agentStream";
import { buildAnswerUserMessage, type UnfinishedEnd } from "./answerMessage";
import { STOPPED_MARK } from "./turnLoop";
import { buildAnswerSystemPrompt } from "./systemPrompt";
import { cutAtFabricatedTurn } from "./fabricatedTurns";
import { withoutToolNames } from "./toolNames";

export type AnswerStreamDeps = {
  question: string;
  toolContext: string[];
  usedToolNames: string[];
  toolNames: readonly string[];
  plannerReply: string | null;
  endedBy?: UnfinishedEnd;
  memory: AgentMemory;
  earlier?: readonly WireMessage[];
  pageContext: string | null;
  model: string | undefined;
  idleTimeoutMs: number;
  stream: (request: StreamRequest, handlers: { onDelta: (accumulated: string) => void }, options: StreamOptions) => Promise<StreamOutcome>;
  onDelta: (accumulated: string) => void;
  signal?: AbortSignal;
};

export type AnswerResult = { text: string | null; cutOff: boolean };

// Stream the synthesized answer (one no-tools completion over the turn's tool results) and write it
// back into conversation memory so a restored conversation renders the same reply. A cut-off stream
// returns what arrived but stays out of memory; with nothing delivered the planner's own reply, then
// a generic fallback, keeps the turn honest.

export const streamAnswer = async (deps: AnswerStreamDeps): Promise<AnswerResult> => {
  const forUser = (text: string) => withoutToolNames(cutAtFabricatedTurn(text).text, deps.toolNames);
  const plannerReply = deps.plannerReply === null ? null : withoutToolNames(deps.plannerReply, deps.toolNames);
  const outcome = await deps.stream(
    {
      messages: [
        { role: "system", content: buildAnswerSystemPrompt(new Date()) },
        ...(deps.pageContext ? [{ role: "system" as const, content: deps.pageContext }] : []),
        {
          role: "user",
          content: buildAnswerUserMessage({
            question: deps.question,
            toolContext: deps.toolContext,
            plannerReply,
            endedBy: deps.endedBy,
            earlier: deps.earlier,
          }),
        },
      ],
      model: deps.model,
    },
    { onDelta: (accumulated) => deps.onDelta(forUser(accumulated)) },
    { idleTimeoutMs: deps.idleTimeoutMs, signal: deps.signal },
  );

  const said = forUser(outcome.text);
  const answerText = said.trim() ? said : null;
  if (outcome.errored) {
    if (answerText !== null && deps.signal?.aborted) {
      const kept = `${answerText}\n\n${STOPPED_MARK}`;
      if (plannerReply !== null) deps.memory.replaceTrailingAssistantReply(kept);
      else deps.memory.pushAssistantReply(kept);
    }
    return { text: answerText ?? plannerReply, cutOff: true };
  }
  if (answerText !== null) {
    if (plannerReply !== null) {
      deps.memory.replaceTrailingAssistantReply(answerText);
    } else {
      deps.memory.pushAssistantReply(answerText);
    }
    return { text: answerText, cutOff: false };
  }
  if (plannerReply !== null) {
    return { text: plannerReply, cutOff: false };
  }
  deps.memory.pushAssistantReply(
    deps.usedToolNames.length ? `Svarte brukeren basert på: ${deps.usedToolNames.join(", ")}` : ANSWERED,
  );
  return { text: ANSWERED, cutOff: false };
};

const ANSWERED = "Svarte brukeren.";
